// ════════════════════════════════════════════════════════════════════════════
// LpaOnboardingTour — tour guiado para cliente novo (4 steps)
// CL-P0-1 do AUDIT_UX_UI_CLIENTE_25ABR.md
//
// Comportamento:
//   • Renderiza nada se localStorage["lpa_client_onboarded"] === "1"
//   • 4 steps: Bem-vindo → Carteira → Performance → Suitability
//   • CTA final: "Começar a usar"
//   • Skip a qualquer momento
//   • Setá flag ao concluir/skipar
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { LpaModal, LpaButton, LpaCard } from "../ui/lpa";

const STORAGE_KEY = "lpa_client_onboarded";

interface Step {
  title: string;
  description: string;
  icon: string;
  highlight?: string;
}

const STEPS: Step[] = [
  {
    title: "Bem-vindo ao LPA Wealth",
    description:
      "Este é seu portal de acompanhamento patrimonial. Aqui você pode visualizar sua carteira, performance, dividendos, IR e muito mais — tudo em tempo real.",
    icon: "👋",
  },
  {
    title: "Sua carteira consolidada",
    description:
      "A aba Carteira mostra suas posições em todas as corretoras (XP, BTG, Inter, Avenue) consolidadas em uma única visão. Clique em qualquer ativo para ver detalhes.",
    icon: "💼",
    highlight: "/carteira",
  },
  {
    title: "Performance e benchmarks",
    description:
      "Acompanhe sua rentabilidade vs CDI, IBOV, IMA-B. Heatmap mensal, drawdown, Sharpe — tudo explicado com tooltips quando você passa o mouse.",
    icon: "📈",
    highlight: "/performance",
  },
  {
    title: "Sua suitability",
    description:
      "Em Perfil, mantenha sua suitability atualizada (vence em 12 meses). Isso garante que sua carteira está alinhada com seu perfil de risco e tolerância.",
    icon: "🎯",
    highlight: "/perfil",
  },
];

export interface LpaOnboardingTourProps {
  /** Forçar exibir mesmo se já concluído (para testes) */
  forceShow?: boolean;
  /** Callback ao concluir */
  onComplete?: () => void;
}

export default function LpaOnboardingTour({
  forceShow = false,
  onComplete,
}: LpaOnboardingTourProps): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (forceShow) {
      setOpen(true);
      return;
    }
    try {
      const done = localStorage.getItem(STORAGE_KEY);
      if (!done) setOpen(true);
    } catch {
      /* SSR/private mode */
    }
  }, [forceShow]);

  const close = (markDone: boolean) => {
    if (markDone) {
      try {
        localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* */
      }
      onComplete?.();
    }
    setOpen(false);
  };

  if (!open) return null;
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <LpaModal
      open={open}
      onClose={() => close(false)}
      title={current.title}
      size="md"
      hideCloseButton={false}
      footer={
        <>
          <LpaButton variant="ghost" onClick={() => close(true)}>
            Pular tour
          </LpaButton>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && (
              <LpaButton variant="secondary" onClick={() => setStep((s) => s - 1)}>
                ← Voltar
              </LpaButton>
            )}
            <LpaButton
              variant="primary"
              onClick={() => (isLast ? close(true) : setStep((s) => s + 1))}
            >
              {isLast ? "Começar a usar →" : "Próximo →"}
            </LpaButton>
          </div>
        </>
      }
      footerAlign="between"
    >
      {/* Progress dots */}
      <div
        style={{
          display: "flex",
          gap: 6,
          justifyContent: "center",
          marginBottom: 20,
        }}
      >
        {STEPS.map((_, i) => (
          <span
            key={i}
            aria-hidden="true"
            style={{
              width: i === step ? 24 : 8,
              height: 8,
              borderRadius: 4,
              background:
                i === step
                  ? "var(--lpa-accent)"
                  : i < step
                    ? "var(--lpa-accent-soft)"
                    : "var(--lpa-border)",
              transition: "all var(--lpa-tr-base)",
            }}
          />
        ))}
      </div>

      <LpaCard variant="glass" padding="lg">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 16,
          }}
        >
          <div
            aria-hidden="true"
            style={{
              fontSize: 56,
              lineHeight: 1,
            }}
          >
            {current.icon}
          </div>
          <p
            style={{
              fontSize: "var(--lpa-text-lg)",
              color: "var(--lpa-text)",
              lineHeight: 1.6,
              margin: 0,
              maxWidth: 460,
            }}
          >
            {current.description}
          </p>
          {current.highlight && (
            <div
              style={{
                fontFamily: "var(--lpa-font-mono)",
                fontSize: "var(--lpa-text-xs)",
                color: "var(--lpa-accent)",
                background: "var(--lpa-accent-soft)",
                padding: "4px 10px",
                borderRadius: "var(--lpa-radius-pill)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Acesse: {current.highlight}
            </div>
          )}
        </div>
      </LpaCard>

      <div
        style={{
          fontFamily: "var(--lpa-font-mono)",
          fontSize: "var(--lpa-text-2xs)",
          color: "var(--lpa-muted)",
          textAlign: "center",
          marginTop: 12,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        Passo {step + 1} de {STEPS.length}
      </div>
    </LpaModal>
  );
}

/**
 * Reset utility (para testes / forçar re-exibição).
 * Pode ser exposto em alguma config "Reabrir tour" no futuro.
 */
export function resetLpaOnboarding(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* */
  }
}
